<template>
  <div class="overview-container">
    <div v-if="form.splits.workouts.length > 1" class="d-flex justify-end mb-4">
      <v-btn
        color="primary"
        depressed
        :outlined="!isSortingSplits"
        :aria-pressed="isSortingSplits"
        min-height="44"
        @click="toggleSplitOrder">
        <v-icon left>{{ isSortingSplits ? "mdi-check" : "mdi-sort" }}</v-icon>
        {{ isSortingSplits ? "Fertig" : "Reihenfolge ändern" }}
      </v-btn>
    </div>
    <section v-if="isSortingSplits" class="split-order" aria-labelledby="split-order-title">
      <h2 id="split-order-title" class="text-h6 font-weight-bold mb-1">Split-Reihenfolge</h2>
      <p class="body-2 grey--text text--lighten-1 mb-4">
        Am Griff ziehen oder mit den Pfeilen verschieben. Diese Reihenfolge wird im Trainingsplan angezeigt.
      </p>
      <draggable
        :list="form.splits.workouts"
        tag="ol"
        class="split-order-list"
        handle=".split-order-handle"
        :force-fallback="true"
        :fallback-on-body="true"
        ghost-class="split-order-ghost"
        @start="closeVisualizationMenus"
        @change="onSplitOrderChange">
        <li v-for="(workout, index) in form.splits.workouts" :key="getSplitKey(workout)" class="split-order-row">
          <span class="split-order-handle" title="Split ziehen" aria-hidden="true"><v-icon>mdi-drag</v-icon></span>
          <span class="split-order-number">{{ index + 1 }}</span>
          <span class="split-order-name">{{ workout.title }}</span>
          <div class="split-order-actions">
            <v-btn
              icon
              width="44"
              height="44"
              :disabled="index === 0"
              :aria-label="workout.title + ' nach oben verschieben'"
              title="Nach oben"
              @click="moveSplit(index, -1)">
              <v-icon>mdi-arrow-up</v-icon>
            </v-btn>
            <v-btn
              icon
              width="44"
              height="44"
              :disabled="index === form.splits.workouts.length - 1"
              :aria-label="workout.title + ' nach unten verschieben'"
              title="Nach unten"
              @click="moveSplit(index, 1)">
              <v-icon>mdi-arrow-down</v-icon>
            </v-btn>
          </div>
        </li>
      </draggable>
      <p class="split-order-announcement" role="status" aria-live="polite" aria-atomic="true">{{ reorderAnnouncement }}</p>
    </section>
    <v-col v-else cols="12" class="pa-0">
      <v-list style="padding: 0" two-line>
        <v-list-item v-for="(workout, index) in form.splits.workouts" :key="getSplitKey(workout)" class="pa-3 split-item mx-1">
          <v-row align="center" no-gutters style="width: 100%">
            <!-- Linke Spalte: Split-Name und Details -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '12' : '3'" :class="{ 'mb-3': $vuetify.breakpoint.smAndDown }">
              <v-list-item-title class="white--text split-title"> Split {{ index + 1 }}: {{ workout.title }} </v-list-item-title>
              <v-list-item-subtitle class="white--text split-overview">
                Übungen: {{ workout.exercises ? workout.exercises.length : 0 }} | Sätze: {{ getTotalSets(workout) }}<br />
                Muskelgruppen:
                <span v-for="(muscle, mIndex) in getActiveMuscles(workout)" :key="mIndex">
                  {{ muscle }}<span v-if="mIndex < getActiveMuscles(workout).length - 1">, </span>
                </span>
              </v-list-item-subtitle>
            </v-col>

            <!-- Mittlere Spalte: Bild und Auswahl -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '4' : '1'" class="ml-2" grow>
              <v-card
                class="workout-card rounded-xl pa-3"
                :style="{
                  '--card-color': workout.fixedVisualization ? workout.fixedVisualization.color : getTrainingPlanVisualization(workout.title).color,
                }">
                <v-img
                  :src="workout.fixedVisualization ? workout.fixedVisualization.img : getTrainingPlanVisualization(workout.title).img"
                  height="100%"
                  width="100%"
                  contain
                  class="d-flex justify-center align-center" />

                <!-- Auswahl-Menü zum Ändern der Visualisierung -->
                <v-menu v-model="menuOpen[index]" offset-y transition="scale-transition">
                  <template #activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" fab absolute color="white" x-small style="top: 2px; right: 2px">
                      <v-icon color="blue" small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>

                  <v-list dark dense>
                    <v-list-item v-for="option in visualizationOptions" :key="option.key" @click="selectVisualization(option, workout, index)">
                      <v-list-item-avatar>
                        <v-img :src="option.img" contain />
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ option.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-card>
            </v-col>

            <!-- Rechte Spalte: Diagramm der aktiven Muskelgruppen -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '7' : '5'" class="d-flex justify-end ml-auto">
              <MuscleDiagram
                noTouch
                height="230px"
                :split="getSplitKey(workout)"
                :hideTagWindows="true"
                background="#252525"
                :exercises="workout.exercises"
                :activeClasses="getActiveMuscles(workout)" />
            </v-col>
          </v-row>
        </v-list-item>
      </v-list>
    </v-col>
  </div>
</template>

<script>
import draggable from "vuedraggable"
import MuscleDiagram from "./PlanRunInteractiveHuman.vue"

export default {
  name: "WorkoutSplitOverviewAndSubmit",
  props: {
    form: { type: Object, required: true },
    handleSubmitPlan: { type: Boolean, default: false },
  },
  components: { MuscleDiagram, draggable },
  computed: {
    isEditing() {
      return this.form && this.form._id
    },
  },
  data() {
    return {
      isSortingSplits: false,
      // View-only identities keep cards and SVG diagrams attached to their split after sorting.
      splitKeys: new WeakMap(this.form.splits.workouts.map((workout, index) => [workout, index])),
      reorderAnnouncement: "",
      menuOpen: this.form.splits.workouts.map(() => false),
      visualizationOptions: [
        { key: "leg", text: "Beine", img: "/img/workout/legSplit.png", color: "#1C4966" },
        { key: "chest", text: "Brust", img: "/img/workout/chestSplit.png", color: "#02734A" },
        { key: "back", text: "Rücken", img: "/img/workout/backSplit.png", color: "#F2B950" },
        { key: "arm", text: "Arm", img: "/img/workout/armSplit.png", color: "#E5735D" },
        { key: "core", text: "Cardio", img: "/img/workout/cardioSplit.png", color: "#F7B5A7" },
        { key: "upper", text: "Oberkörper", img: "/img/workout/upperBodySplit.png", color: "#A3C6C4" },
      ],
    }
  },
  methods: {
    toggleSplitOrder() {
      this.closeVisualizationMenus()
      this.reorderAnnouncement = ""
      this.isSortingSplits = !this.isSortingSplits
    },
    getSplitKey(workout) {
      return this.splitKeys.get(workout)
    },
    closeVisualizationMenus() {
      this.menuOpen = this.form.splits.workouts.map(() => false)
    },
    announceSplitMove(workout, index) {
      this.closeVisualizationMenus()
      this.reorderAnnouncement = `${workout.title} ist jetzt an Position ${index + 1} von ${this.form.splits.workouts.length}.`
    },
    onSplitOrderChange(event) {
      if (event.moved) this.announceSplitMove(event.moved.element, event.moved.newIndex)
    },
    moveSplit(index, offset) {
      const workouts = this.form.splits.workouts
      const targetIndex = index + offset
      if (!Number.isInteger(index) || !Number.isInteger(offset) || index < 0 || index >= workouts.length || targetIndex < 0 || targetIndex >= workouts.length) return
      const [workout] = workouts.splice(index, 1)
      workouts.splice(targetIndex, 0, workout)
      this.announceSplitMove(workout, targetIndex)
    },
    submitPlan() {
      // Ensure every workout has a fixed visualization before submitting
      this.form.splits.workouts.forEach((workout) => {
        if (!workout.fixedVisualization) {
          const { img, color } = this.getTrainingPlanVisualization(workout.title)
          this.$set(workout, "fixedVisualization", { img, color })
        }
      })

      // MODIFIED: Emit a different event based on whether we are editing or creating
      if (this.isEditing) {
        console.log("Updating existing plan with form data:", this.form)
        this.$emit("updatePlan", this.form)
      } else {
        console.log("Submitting new plan with form data:", this.form)
        this.$emit("submitPlan", this.form)
      }
    },
    selectVisualization(option, workout, index) {
      this.$set(workout, "fixedVisualization", { img: option.img, color: option.color })
      this.$set(this.menuOpen, index, false)
    },
    getTrainingPlanVisualization(title) {
      const armSplitKeywords = [
        "arm",
        "arme",
        "bizeps",
        "trizeps",
        "bizepstraining",
        "trizepstraining",
        "oberarm",
        "unterarm",
        "armtraining",
        "armübung",
        "armübungen",
        "arm-workout",
        "arm-muskulatur",
        "arms",
        "biceps",
        "triceps",
        "upper arm",
        "forearm",
        "arm training",
        "arm workout",
        "arm exercises",
        "arm muscles",
      ]
      const legSplitKeywords = [
        "bein",
        "beine",
        "beinarbeit",
        "unterkörper",
        "oberschenkel",
        "waden",
        "hamstrings",
        "quadrizeps",
        "beintraining",
        "beinübungen",
        "bein-workout",
        "leg",
        "legs",
        "leg day",
        "leg workout",
        "leg training",
        "thigh",
        "calves",
        "hamstrings",
        "quads",
        "lower body",
        "leg exercises",
      ]
      const chestSplitKeywords = [
        "brust",
        "push",
        "brusttraining",
        "brustübungen",
        "brust-workout",
        "pectoralis",
        "brustmuskulatur",
        "chest",
        "chest training",
        "chest workout",
        "pectoral",
        "pecs",
        "chest exercises",
      ]
      const backSplitKeywords = [
        "rücken",
        "pull",
        "rückentraining",
        "rückenübungen",
        "rücken-workout",
        "lats",
        "latissimus",
        "trapez",
        "trapezius",
        "back",
        "back training",
        "back workout",
        "upper back",
        "lower back",
        "lats",
        "latissimus",
        "trapezius",
        "back exercises",
      ]
      const shoulderSplitKeywords = [
        "schulter",
        "schultertraining",
        "schulterübungen",
        "schulter-workout",
        "deltoid",
        "deltoideus",
        "shoulder",
        "shoulder training",
        "shoulder workout",
        "deltoid",
        "deltoids",
        "shoulder exercises",
      ]
      const coreSplitKeywords = [
        "bauch",
        "bauchtraining",
        "bauchübungen",
        "bauch-workout",
        "sechpack",
        "magen",
        "core",
        "abs",
        "abdominals",
        "abs workout",
        "ab exercises",
        "sixpack",
        "ober",
      ]
      const lowerName = title.toLowerCase()
      if (legSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "leg")
      if (chestSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "chest")
      if (backSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "back")
      if (shoulderSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "upper")
      if (armSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "arm")
      if (coreSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "core")
      return this.visualizationOptions.find((o) => o.key === "upper") // Default to upper body if no match
    },
    getTotalSets(workout) {
      if (!workout.exercises) return 0
      return workout.exercises.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0)
    },
    getActiveMuscles(workout) {
      const muscleSet = new Set()
      if (!workout.exercises) return []
      for (let exercise of workout.exercises) {
        if (exercise.tags) {
          for (let tag of exercise.tags) {
            if (tag.class) {
              muscleSet.add(tag.class)
            }
          }
        }
      }
      return Array.from(muscleSet)
    },
  },
  watch: {
    handleSubmitPlan(newValue) {
      console.log("oiii ha was beho bim submit", newValue)
      if (newValue === true) {
        this.$emit("resetSubmitPlan")
        this.submitPlan()
      }
    },
  },
}
</script>
<style lang="scss" scoped>
/* GRUNDLAGEN & CONTAINER */
.overview-container {
  touch-action: pan-y;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 20px 20px 100px;
  background: #121212; // Dunklerer, moderner Hintergrund
}

.split-order {
  margin-bottom: 28px;
}

.split-order-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.split-order-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px;
  color: white;
  background: #252525;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
}

.split-order-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  height: 44px;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.split-order-handle:active {
  cursor: grabbing;
}

.split-order-number {
  flex: 0 0 24px;
  text-align: center;
  color: #b9d9c5;
  font-weight: 700;
}

.split-order-name {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: 600;
}

.split-order-actions {
  display: flex;
  flex-shrink: 0;
}

.split-order-ghost {
  opacity: 0.35;
}

.split-order-announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}

@media (max-width: 600px) {
  .overview-container {
    padding: 8px 0 100px;
  }

  .split-order-row {
    gap: 4px;
    padding: 4px;
  }
}

/* LISTE & LISTEN-ELEMENTE */
.v-list {
  padding: 0;
}

.split-item {
  background: rgba(30, 30, 30, 0.7); // Semi-transparenter Hintergrund
  backdrop-filter: blur(15px); // "Frosted Glass" Effekt
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px !important; // Stärker abgerundete Ecken
  padding: 24px !important;
  margin-bottom: 20px !important;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden; // Wichtig für die Glow-Effekte
  position: relative; // Wichtig für die Glow-Effekte

  // Glow-Effekt, der von der Kartenfarbe abhängt
  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background-image: radial-gradient(circle, color-mix(in srgb, var(--card-color, #424242) 20%, transparent 60%), transparent);
    transform: rotate(0deg);
    transition: transform 1s ease-in-out;
  }

  &:hover::before {
    transform: rotate(90deg);
  }
}

/* TITEL & TEXTSTILE */
.white--text {
  color: white !important;
}

.split-title {
  font-size: 1.5rem; // Grösser für mehr Präsenz
  font-weight: 700;
  letter-spacing: -0.02em; // Moderneres Kerning
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5); // Bessere Lesbarkeit
}

.split-overview {
  font-size: 0.95rem;
  opacity: 0.8;
  line-height: 1.6; // Besserer Zeilenabstand
}

/* WORKOUT-KARTE (mit dem Körperteil-Bild) */
.workout-card {
  border-radius: 18px !important; // Passend zum neuen Stil
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  // Farbverlauf, der die Karte lebendiger macht
  background: linear-gradient(135deg, var(--card-color), color-mix(in srgb, var(--card-color) 70%, #1e1e1e 100%));

  &:hover {
    transform: scale(1.05) rotate(2deg);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  }
}

/* MUSKEL-DIAGRAMM */
.muscle-diagram-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

// Stellt sicher, dass das Diagramm im Container zentriert ist
::v-deep .MuscleDiagram {
  transform: scale(1.1); // Macht das Diagramm etwas grösser
}

/* FOOTER & BUTTON */
.footer-actions {
  padding: 24px 0;
}
</style>
